"use client";

import React, {useEffect, useRef, useState} from "react";
import {Card} from "@heroui/card";
import {Slider} from "@heroui/slider";
import {Spinner} from "@heroui/react";
import {useMonitoring} from "@/components/monitoring";
import {soundIcons, soundLabels, soundNames, type SoundName} from "./icons";

const SOUNDS: { name: SoundName; icon: string; audioUrl: string }[] = soundNames.map((name) => ({
    name,
    icon: soundIcons[name],
    // audioUrl: `/sounds/${name}/concatenated.mp3`,
    audioUrl: `https://statics.kick-video.download/sounds/${name}/concatenated.mp3`,
}));

interface SoundTileProps {
    name: SoundName;
    icon: string;
    audioUrl: string;
    globalVolume: number;
}

function SoundTile({name, icon, audioUrl, globalVolume}: SoundTileProps) {
    const {track} = useMonitoring();
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [localVolume, setLocalVolume] = useState(1);

    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.volume = localVolume * globalVolume;
        }
    }, [localVolume, globalVolume]);

    const togglePlay = () => {
        if (isPlaying || isLoading) {
            audioRef.current?.pause();
            setIsPlaying(false);
            setIsLoading(false);
            track("Ambient Sound Stopped", {sound: name});
        } else {
            if (!audioRef.current) {
                const audio = new Audio(audioUrl);
                audio.loop = true;
                audio.addEventListener("waiting", () => setIsLoading(true));
                audio.addEventListener("playing", () => {
                    setIsLoading(false);
                    setIsPlaying(true);
                });
                audio.addEventListener("error", () => {
                    setIsLoading(false);
                    setIsPlaying(false);
                    track("Ambient Sound Error", {sound: name});
                });
                audioRef.current = audio;
            }
            audioRef.current.volume = localVolume * globalVolume;
            setIsLoading(true);
            // A pause() before play() resolves rejects with AbortError; swallow it.
            audioRef.current.play().catch(() => setIsLoading(false));
            track("Ambient Sound Played", {sound: name});
        }
    };

    return (
        <Card
            isPressable
            onPress={togglePlay}
            aria-label={`${isPlaying ? "Pause" : "Play"} ${soundLabels[name]}`}
            aria-pressed={isPlaying}
            aria-busy={isLoading}
            className={`flex min-h-[170px] flex-col items-center justify-center gap-3 p-5 text-center transition ${
                isPlaying
                    ? "border-2 border-primary bg-primary/15 shadow-lg shadow-primary/30"
                    : "border border-default-200 hover:border-default-400"
            }`}
        >
            <div
                aria-hidden="true"
                className={`flex h-12 w-12 items-center justify-center transition ${
                    isPlaying ? "text-primary" : "text-default-500"
                }`}
            >
                {isLoading ? (
                    <Spinner size="sm" color="primary" aria-label={`Loading ${soundLabels[name]}`}/>
                ) : (
                    <span
                        className="flex h-full w-full items-center justify-center"
                        dangerouslySetInnerHTML={{__html: icon}}
                    />
                )}
            </div>
            <span className="w-full text-center text-sm font-medium text-default-600">
                {soundLabels[name]}
            </span>
            <div
                className="w-4/5"
                role="presentation"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
            >
                <Slider
                    aria-label={`${soundLabels[name]} volume`}
                    size="sm"
                    color="primary"
                    minValue={0}
                    maxValue={1}
                    step={0.01}
                    value={localVolume}
                    onChange={(v) => setLocalVolume(Array.isArray(v) ? v[0] : v)}
                    onChangeEnd={(v) =>
                        track("Ambient Sound Volume Changed", {
                            sound: name,
                            volume: Array.isArray(v) ? v[0] : v,
                        })
                    }
                />
            </div>
        </Card>
    );
}

function orderSounds(order: string[]) {
    const byName = new Map(SOUNDS.map((s) => [s.name, s]));
    const seen = new Set<SoundName>();
    const ordered: typeof SOUNDS = [];
    for (const name of order) {
        const sound = byName.get(name as SoundName);
        if (sound && !seen.has(sound.name)) {
            ordered.push(sound);
            seen.add(sound.name);
        }
    }
    for (const sound of SOUNDS) {
        if (!seen.has(sound.name)) ordered.push(sound);
    }
    return ordered;
}

export function App({order = []}: { order?: string[] }) {
    const {track} = useMonitoring();
    const [globalVolume, setGlobalVolume] = useState(1);
    const globalPercent = Math.round(globalVolume * 100);
    const sounds = orderSounds(order);

    return (
        <section className="min-h-screen p-10" aria-labelledby="ambient-player-title">
            <Card
                className="mx-auto mb-10 flex max-w-[280px] flex-row items-center gap-3 rounded-full px-5 py-3"
                role="group"
                aria-label="Master volume"
            >
                <svg
                    aria-hidden="true"
                    focusable="false"
                    className="h-[18px] w-[18px] flex-shrink-0 text-default-500"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
                </svg>
                <Slider
                    aria-label="Master volume"
                    className="flex-1"
                    size="sm"
                    color="primary"
                    minValue={0}
                    maxValue={1}
                    step={0.01}
                    value={globalVolume}
                    onChange={(v) => setGlobalVolume(Array.isArray(v) ? v[0] : v)}
                    onChangeEnd={(v) =>
                        track("Ambient Master Volume Changed", {
                            volume: Array.isArray(v) ? v[0] : v,
                        })
                    }
                />
                <span
                    aria-hidden="true"
                    className="whitespace-nowrap text-xs font-medium uppercase tracking-wider text-default-500"
                >
                    {globalPercent}%
                </span>
            </Card>

            <div
                role="group"
                aria-label="Ambient sounds"
                className="mx-auto grid max-w-[1100px] grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-5"
            >
                {sounds.map((sound) => (
                    <SoundTile
                        key={sound.name}
                        name={sound.name}
                        icon={sound.icon}
                        audioUrl={sound.audioUrl}
                        globalVolume={globalVolume}
                    />
                ))}
            </div>
        </section>
    );
}
